import {
  Component,
  OnInit
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute, ActivatedRouteSnapshot, Data } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';

export interface PageTrailItem {
  label: string;
  url: string;
}

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss']
})
export class AppBreadcrumbComponent implements OnInit {
  // @Input() layout;
  pageInfo: Data = Object.create(null);
  trail: PageTrailItem[] = [];

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title
  ) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .pipe(map(() => this.activatedRoute))
      .pipe(
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        })
      )
      .pipe(filter(route => route.outlet === 'primary'))
      .pipe(mergeMap(route => route.data))
      .subscribe(event => {
        this.titleService.setTitle(event['title']);
        this.pageInfo = event;
        this.trail = this.buildTrail(this.router.routerState.snapshot.root);
      });
  }

  ngOnInit() { }

  /**
   * Breadcrumb trail: Home > the page's parents > the page.
   *
   * The page is the deepest route carrying data.breadcrumb. Module groupings
   * ("Assets", "Workflow", ...) are not crumbs: the trail follows the menu,
   * where e.g. Assignments is a page of its own. A page below a list names
   * its list in data.breadcrumbParents ([{ label, url }], with ':param'
   * filled from the route), giving e.g. Home > Vouchers > Details.
   */
  private buildTrail(root: ActivatedRouteSnapshot): PageTrailItem[] {
    const chain = this.buildChain(root);
    const page = chain[chain.length - 1];
    if (!page) return [];

    let leaf: ActivatedRouteSnapshot = root;
    while (leaf.firstChild) leaf = leaf.firstChild;
    const fill = (url: string) => url.replace(/:(\w+)/g, (m, name) => leaf.paramMap.get(name) ?? m);
    const parents: PageTrailItem[] = (leaf.data?.['breadcrumbParents'] || [])
      .map((p: PageTrailItem) => ({ label: p.label, url: fill(p.url) }));

    return [...parents, page];
  }

  /**
   * Each route's data.breadcrumb, root to leaf. A child inherits its parent's
   * data when it sets none, so repeated labels collapse.
   */
  private buildChain(root: ActivatedRouteSnapshot): PageTrailItem[] {
    const trail: PageTrailItem[] = [];
    let url = '';
    let route: ActivatedRouteSnapshot | null = root.firstChild;
    while (route) {
      const segment = route.url.map(s => s.path).join('/');
      if (segment) url += `/${segment}`;
      const label = route.data?.['breadcrumb'];
      // An empty-path child (a module's landing page) is the same page as its
      // parent, so it keeps the parent's crumb rather than adding a second one.
      const sameAsParent = !segment && trail.length > 0;
      if (label && !sameAsParent) {
        const previous = trail[trail.length - 1];
        if (previous && previous.label === label) {
          previous.url = url || '/';
        } else {
          trail.push({ label, url: url || '/' });
        }
      }
      route = route.children.find(c => c.outlet === 'primary') || null;
    }
    return trail;
  }
}
